import {scopedHandler} from 'utils/redux';
import ns from './namespace';
import initState from './state';
import {getFormValues} from 'redux-form';
import * as actions from './actions';
import * as effects from './effects';
import {decorateHandler as lifecycle} from 'fragments/lifecycle';
import {
	getBuildingsType,
	formatUrlQuery,
	hasBuildingsSearchCriteria,
	checkCsvUniqIdExpiration,
} from './utils';
import {pushQuery, replaceQuery} from 'io/history';
import {mergeLeft, always, contains, reject, append, equals} from 'ramda';
import removeUnassignedProperties from 'utils/remove-unassigned-props';
import {defaultBuildingsQuery, emptyBuildingsFilters} from './constants';
import {emptyPagination} from 'constants/pagination';

const resetResults = {
	processing: false,
	buildingsLoading: false,
	buildingsNotYetLoaded: true,
	buildings: [],
	buildingsPagination: emptyPagination,
	massSelection: [],
};

const canFetch = state =>
	!state.buildingsNotYetLoaded &&
	hasBuildingsSearchCriteria(state.buildingsQuery, state.buildingsType);

const handler = scopedHandler(ns, (state = initState, fullState, {type, payload}) => {
	switch (type) {
		case actions.initialize.type: {
			return [state, effects.initialize()];
		}

		case actions.restoreQuery.type: {
			return [state, effects.restoreQuery(payload === true)];
		}

		case actions.destroy.type: {
			return [initState, effects.destroy()];
		}

		case actions.recheckQuery.type: {
			const reInit = payload;
			if (!reInit && !canFetch(state)) return [state, null];
			const shouldUpdate = !reInit && canFetch(state);

			const newState = {
				...state,
				// Only update the processing statuses if we are actually updating the list
				// In some cases there might a request already happening which just updated the state (and this would overwrite it)
				processing: shouldUpdate ? true : state.processing,
				buildingsLoading: shouldUpdate ? true : state.buildingsLoading,
				buildingsNotYetLoaded: reInit,
				buildings: reInit ? [] : state.buildings,
				buildingsPagination: emptyPagination,
			};
			return [newState, shouldUpdate ? effects.requestingBuildingsByQuery() : null];
		}

		case actions.changePage.type: {
			const buildingsQuery = {...state.buildingsQuery, _page: payload};
			const newState = {
				...state,
				buildingsLoading: canFetch(state),
				buildingsQuery,
			};

			const urlQuery = formatUrlQuery({buildingsQuery});
			pushQuery(mergeLeft(removeUnassignedProperties(urlQuery)));

			return [newState, canFetch(state) ? effects.requestingBuildingsByQuery() : null];
		}

		case actions.setBuildingsType.type: {
			const q = getBuildingsType(payload);

			const newQuery = {
				...state.buildingsQuery,
				...q,
				_page: '1',
			};

			const urlQuery = formatUrlQuery({buildingsQuery: newQuery});
			replaceQuery(always(removeUnassignedProperties(urlQuery)));

			return [
				{
					...state,
					...resetResults,
					buildingsType: payload,
					buildingsQuery: newQuery,
				},
				null,
			];
		}

		case actions.setPerPage.type: {
			const newQuery = {
				...state.buildingsQuery,
				_limit: payload,
				_page: '1',
			};

			const urlQuery = formatUrlQuery({buildingsQuery: newQuery});
			pushQuery(mergeLeft(removeUnassignedProperties(urlQuery)));

			return [
				{
					...state,
					buildingsQuery: newQuery,
					processing: canFetch(state),
					buildingsLoading: canFetch(state),
				},
				canFetch(state) ? effects.requestingBuildingsByQuery() : null,
			];
		}

		case actions.setSort.type: {
			const newQuery = {
				...state.buildingsQuery,
				_sortColumn: payload,
			};

			const urlQuery = formatUrlQuery({buildingsQuery: newQuery});
			pushQuery(mergeLeft(removeUnassignedProperties(urlQuery)));

			return [
				{
					...state,
					buildingsQuery: newQuery,
					processing: canFetch(state),
					buildingsLoading: canFetch(state),
				},
				canFetch(state) ? effects.requestingBuildingsByQuery() : null,
			];
		}

		case actions.setSortDir.type: {
			const newQuery = {
				...state.buildingsQuery,
				_sortDirection: payload,
			};

			const urlQuery = formatUrlQuery({buildingsQuery: newQuery});
			pushQuery(mergeLeft(removeUnassignedProperties(urlQuery)));

			return [
				{
					...state,
					buildingsQuery: newQuery,
					processing: canFetch(state),
					buildingsLoading: canFetch(state),
				},
				canFetch(state) ? effects.requestingBuildingsByQuery() : null,
			];
		}

		case actions.setQuery.type: {
			const values = checkCsvUniqIdExpiration(
				getFormValues('buildingsFilterForm')(fullState) || {},
			);
			if (!hasBuildingsSearchCriteria(values, state.buildingsType)) return [state, null];
			const newQuery = {
				...defaultBuildingsQuery,
				...state.buildingsQuery,
				...emptyBuildingsFilters,
				...values,
				...getBuildingsType(state.buildingsType, values.types),
				_page: '1',
			};
			delete newQuery._time;
			delete newQuery.q;
			replaceQuery(
				always(removeUnassignedProperties(formatUrlQuery({buildingsQuery: newQuery}))),
			);
			return [
				{
					...state,
					buildingsQuery: newQuery,
					processing: true,
					buildingsLoading: true,
					buildingsNotYetLoaded: false,
					massSelection: [],
				},
				effects.requestingBuildingsByQuery(),
			];
		}

		case actions.clearQuery.type: {
			const newQuery = {
				...defaultBuildingsQuery,
				...emptyBuildingsFilters,
				...getBuildingsType(state.buildingsType),
			};
			replaceQuery(
				always(removeUnassignedProperties(formatUrlQuery({buildingsQuery: newQuery}))),
			);
			return [
				{...state, ...resetResults, buildingsQuery: newQuery},
				effects.resetFilters(newQuery),
			];
		}

		case actions.toggleMassEditor.type: {
			return [
				{...state, massEditorOpen: payload ? payload : !state.massEditorOpen},
				null,
			];
		}

		case actions.selectBuilding.type: {
			const selection = state.massSelection;

			const newSelection = contains(payload, selection)
				? reject(equals(payload), selection)
				: append(payload, selection);

			return [{...state, massSelection: newSelection}, null];
		}

		case actions.clearMassSelection.type: {
			return [{...state, massSelection: []}, null];
		}

		case actions.massSelect.type: {
			return [state, effects.requestingBuildingsIdsByQuery()];
		}

		case actions.updateSelectedBuildings.type: {
			return [
				state,
				effects.updatingSelectedBuildings({
					payload,
					ids: state.massSelection,
					selectionQuery: state.buildingsQuery,
				}),
			];
		}

		case actions.openAreasSelector.type: {
			return [{...state, areasSelectorOpen: true}, effects.initAreasMap()];
		}

		case actions.closeAreasSelector.type: {
			const newState = {
				...state,
				areasSelectorOpen: false,
				areasSelectorSearch: '',
			};
			return [newState, null];
		}

		case actions.setAreasSelectorType.type: {
			return [state, effects.setAreasMapType(payload)];
		}

		case actions.setAreasSelectorSearch.type: {
			return [{...state, areasSelectorSearch: payload}, null];
		}

		case actions.openAreasSelectorSuggestion.type: {
			return [state, effects.openAreasSelectorSuggestion(payload)];
		}

		case actions.openCsvFileSelector.type: {
			return [{...state, csvFileSelectorOpen: true}, null];
		}

		case actions.closeCsvFileSelector.type: {
			const newState = {
				...state,
				csvFileSelectorOpen: false,
			};
			return [newState, null];
		}

		case actions.csvFileSelectorUpload.type: {
			return [
				{
					...state,
					csvFileSelectorProcessing: true,
				},
				effects.handleCsvFileSelectorUpload(payload),
			];
		}

		case actions._csvFileSelectorUpload.type: {
			return [
				{
					...state,
					csvFileSelectorProcessing: false,
				},
				null,
			];
		}
		case actions._initialize.type: {
			return [{...state, initialized: true}, null];
		}

		case actions._startOp.type: {
			return [{...state, processing: true}, null];
		}

		case actions._opFailed.type: {
			return [{...state, processing: false, buildingsLoading: false}, null];
		}

		case actions._opOk.type: {
			return [{...state, processing: false}, null];
		}

		case actions.updateMetaData.type: {
			const types = (payload.query.types || '').split(',').filter(Boolean);
			const newQuery = {
				...defaultBuildingsQuery,
				...emptyBuildingsFilters,
				...payload.query,
				...getBuildingsType(payload.type, types),
			};
			delete newQuery._time;
			const shouldFetch =
				payload.updateList && hasBuildingsSearchCriteria(newQuery, payload.type);
			return [
				{
					...state,
					...resetResults,
					processing: !!shouldFetch,
					buildingsLoading: !!shouldFetch,
					buildingsNotYetLoaded: !shouldFetch,
					buildingsType: payload.type,
					contactUpdater: payload.contactUpdater,
					buildingsQuery: newQuery,
				},
				[
					effects.resetFilters(newQuery),
					shouldFetch ? effects.requestingBuildingsByQuery() : null,
				],
			];
		}

		case actions._setBuildings.type: {
			const newState = {
				...state,
				buildings: payload.data,
				buildingsPagination: payload.pagination,
				buildingsLoading: false,
				processing: false,
			};

			return [newState, null];
		}

		case actions._setMassSelection.type: {
			const newState = {
				...state,
				processing: false,
				massSelection: payload,
			};

			return [newState, null];
		}

		case actions._resetMassEditor.type: {
			const newState = {
				...state,
				processing: false,
				massSelection: [],
			};

			return [newState, null];
		}

		case actions.getAvailableTags.type: {
			return [state, effects.getAvailableTags()];
		}

		case actions._getAvailableTags.type: {
			return [
				{
					...state,
					availableTags: payload,
				},
				null,
			];
		}

		case actions.searchProducts.type: {
			return [state, effects.getProducts(payload)];
		}

		default:
			return [state, null];
	}
});

export default lifecycle({
	namespace: ns,
	initializeType: actions.initialize.type,
	destroyType: actions.destroy.type,
})(handler);
